import { ArrowLeft, Eye, EyeClosed } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useIsDesktopViewport } from '../../../hooks/useIsDesktopViewport';
import { emailSignIn, getAuthError, getUserLoadingState, setAuthFlow, toggleAuthModal } from '../../../store/slices/authSlice';
import { gaEvents } from '../../../utils/analytics';
import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import OAuthButtons from './OAuthButtons';
import styles from './styles.module.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignInStep = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isDesktop = useIsDesktopViewport();
    const loading = useSelector(getUserLoadingState);
    const error = useSelector(getAuthError);
    // HORO-10509: show/hide toggle for the password field's typed input —
    // same eye/eye-closed pattern as Set password and Reset password's new-
    // password fields.
    const [showPassword, setShowPassword] = useState(false);
    const {
        register,
        handleSubmit,
        trigger,
        formState: { errors, isValid, touchedFields }
    } = useForm({ mode: 'onChange' });

    // See SetPasswordStep's identical note — isValid otherwise reports
    // true on mount before the fields have been validated.
    useEffect(() => {
        trigger();
    }, [trigger]);

    const onSubmit = ({ email, password }) => {
        if (loading) {
            return;
        }
        dispatch(emailSignIn({ email: email.trim().toLowerCase(), password }));
    };

    // HORO-10509: back now fully dismisses the modal — Sign in is its own
    // full-height view (see AuthModal.js), not a step within the wizard
    // anymore, so "back" means returning to whatever page was underneath,
    // not stepping back to the Entry/signup screen. Same on desktop — the
    // card's arrow behaves identically, confirmed via Figma (there's no
    // "previous step" before Sign in on either breakpoint).
    const onBack = () => dispatch(toggleAuthModal({ trigger: false }));

    // HORO-10509: desktop card's field labels are Roboto 400/16px
    // (confirmed via Figma inspector on "Registeration_general" — Welcome
    // back, desktop) vs mobile's bold/14px `.fieldLabel` — same override
    // already built for Set password's desktop labels.
    const fieldLabelClassName = isDesktop ? `${styles.fieldLabel} ${styles.fieldLabelDesktop}` : styles.fieldLabel;

    return (
        <>
            <div className={styles.signInHeader}>
                <button type="button" className={styles.backIconBtn} aria-label={t('buttons.back')} onClick={onBack}>
                    <ArrowLeft size={24} color="#FFFFFF" />
                </button>
                <h2 className={`${styles.title} ${styles.titleMixedCase}`}>{t('modal.signInTitle')}</h2>
                {/* Balances the back button's width so the title centers on
                    the full row, not just the space left of the button. */}
                <div className={styles.headerSpacer} aria-hidden="true" />
            </div>
            {/* Desktop-only subtitle — mobile's Figma frame has none. */}
            {isDesktop && <p className={styles.subtitle}>{t('modal.signInSubtitleDesktop')}</p>}
            <form onSubmit={handleSubmit(onSubmit)}>
                <span className={fieldLabelClassName}>{t('modal.emailFieldLabel')}</span>
                <div className={styles.inputWrapper}>
                    <input
                        className={styles.fieldInput}
                        type="email"
                        // "username", not "email" — this field is paired
                        // with a password field for sign-in, and browsers/
                        // password managers key saved-login autofill off
                        // "username" even when the value is an email
                        // address. EntryStep/ForgotPasswordStep/AddEmailStep
                        // use "email" instead since none of those pair an
                        // email field with a password field.
                        autoComplete="username"
                        placeholder={t('modal.emailPlaceholder')}
                        {...register('email', { required: true, pattern: EMAIL_REGEX })}
                    />
                    {errors.email && touchedFields.email && (
                        <span className={styles.error}>{t('errors.invalidEmail', 'Enter a valid email address')}</span>
                    )}
                </div>
                <span className={fieldLabelClassName}>{t('modal.passwordFieldLabel')}</span>
                <div className={`${styles.inputWrapper} ${styles.inputWrapperTight}`}>
                    <div className={styles.passwordFieldWrap}>
                        <input
                            className={`${styles.fieldInput} ${styles.fieldInputWithToggle}`}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder={t('modal.enterPasswordPlaceholder')}
                            {...register('password', { required: true })}
                        />
                        <button
                            type="button"
                            className={styles.passwordToggleBtn}
                            aria-label={showPassword ? t('buttons.hidePassword') : t('buttons.showPassword')}
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? <EyeClosed size={20} color="#FFFFFF" /> : <Eye size={20} color="#FFFFFF" />}
                        </button>
                    </div>
                </div>
                {/* HORO-10509: always mounted (not `error &&`) so this slot's
                    height is reserved up front — mounting it only when an
                    error appears was pushing sign-in's already-tall content
                    past the fixed-height card/full-screen container and
                    triggering its overflow-y:auto scrollbar. See
                    .formErrorReserved in styles.module.scss. */}
                <p className={`${styles.formError} ${styles.formErrorReserved}`}>{error?.message || ''}</p>
                <div className={styles.submitBtnWrap}>
                    <CustomButton
                        text={t('buttons.continue')}
                        loading={loading}
                        disable={loading || !isValid}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                    />
                </div>
            </form>
            <button type="button" className={styles.forgotPasswordLink} onClick={() => dispatch(setAuthFlow('forgot-password'))}>
                {t('buttons.forgotPassword')}
            </button>
            {/* HORO-10509: no longer hidden on email focus — the view just
                scrolls if content overflows (see AuthModal's overflow-y:auto). */}
            <OAuthButtons compact desktop={isDesktop} />
            {/* HORO-10509: "Don't have an account? Sign up" — shown on both
                breakpoints now, same .helperText/.helperTextAction pattern
                EntryStep uses for its own "Already have an account?" footer. */}
            <p className={`${styles.helperText} ${styles.helperTextSpaced}`}>
                {t('modal.noAccount')}{' '}
                <button
                    type="button"
                    className={styles.helperTextAction}
                    onClick={() => {
                        // HORO-10509: the only other place authFlow becomes
                        // 'entry' is via toggleAuthModal (hooked centrally in
                        // authSlice.js) or the SetPasswordStep/VerifyCodeStep
                        // "back to edit email" links — those are backward
                        // navigation within an already-started register flow,
                        // not a fresh register click, so they're deliberately
                        // NOT instrumented. This is the one setAuthFlow('entry')
                        // dispatch that is a genuine register-click.
                        gaEvents.authRegisterClick();
                        dispatch(setAuthFlow('entry'));
                    }}
                >
                    {t('modal.signUpLink')}
                </button>
            </p>
        </>
    );
};

export default SignInStep;
